import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  LinearProgress,
  Button,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import Client from './../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatMoney(value) {
  const numberValue = Number(value || 0);

  if (numberValue >= 1000000) {
    return thousandsSeparator((numberValue / 1000000).toFixed(1)) + "M";
  }

  if (numberValue >= 1000) {
    return thousandsSeparator((numberValue / 1000).toFixed(1)) + "K";
  }

  return thousandsSeparator(numberValue.toFixed(0));
}

class ClientsActivityByLocation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      data: [],
      period: {},
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.periodMode !== this.props.periodMode) {
      this.loadData();
    }
  }

  loadData = () => {
    const queryData = {
      periodMode: this.props.periodMode || 'last_30d',
    };

    const data = BackofficeReportsApi.getClientsRegisteredActivityByLocation(this.state.client, queryData);

    if (data) {
      this.setState({
        data: data.rows || [],
        period: data.period || {},
      });
    } else {
      this.setState({
        data: [],
        period: {},
      });
    }
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const cleanValue = String(value).replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.state.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';
    const periodLabel = this.state.period?.label || periodMode;

    const totalRegisteredOrders = rows.reduce(
      (sum, row) => sum + Number(row.registered_orders || 0),
      0
    );

    const totalRegisteredSales = rows.reduce(
      (sum, row) => sum + Number(row.registered_value || 0),
      0
    );

    const totalOrders = rows.reduce(
      (sum, row) => sum + Number(row.total_orders || 0),
      0
    );

    const totalSales = rows.reduce(
      (sum, row) => sum + Number(row.total_value || 0),
      0
    );

    const anonymousOrders = Math.max(totalOrders - totalRegisteredOrders, 0);
    const anonymousSales = Math.max(totalSales - totalRegisteredSales, 0);

    const weightedRegisteredOrderShare = totalOrders > 0
      ? (totalRegisteredOrders / totalOrders) * 100
      : 0;

    const weightedRegisteredSalesShare = totalSales > 0
      ? (totalRegisteredSales / totalSales) * 100
      : 0;

    const weightedRegisteredAov = totalRegisteredOrders > 0
      ? totalRegisteredSales / totalRegisteredOrders
      : 0;

    const weightedAnonymousAov = anonymousOrders > 0
      ? anonymousSales / anonymousOrders
      : 0;

    const topRegisteredLocation = [...rows].sort(
      (a, b) => Number(b.registered_value || 0) - Number(a.registered_value || 0)
    )[0];

    const headers = [
      { key: 'location_name', label: 'Location' },
      { key: 'registered_orders', label: 'Registered orders' },
      { key: 'registered_value', label: 'Registered sales' },
      { key: 'registered_order_share', label: 'Registered order share %' },
      { key: 'registered_value_share', label: 'Registered sales share %' },
      { key: 'registered_aov', label: 'Registered AOV' },
      { key: 'anonymous_aov', label: 'Anonymous AOV' },
      { key: 'total_orders', label: 'Total orders' },
      { key: 'total_value', label: 'Total sales' },
    ];

    const csvRows = [
      ['Registered activity by location'].map((value) => this.csvValue(value)).join(','),
      ['Period', periodLabel].map((value) => this.csvValue(value)).join(','),
      ['Period mode', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total registered orders', totalRegisteredOrders].map((value) => this.csvValue(value)).join(','),
      ['Total registered sales', totalRegisteredSales.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total orders', totalOrders].map((value) => this.csvValue(value)).join(','),
      ['Total sales', totalSales.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted registered order share %', `${weightedRegisteredOrderShare.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Weighted registered sales share %', `${weightedRegisteredSalesShare.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Weighted registered AOV', weightedRegisteredAov.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted anonymous AOV', weightedAnonymousAov.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Top location by registered sales', topRegisteredLocation ? topRegisteredLocation.location_name || 'Unknown' : ''].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (
              header.key === 'registered_order_share' ||
              header.key === 'registered_value_share'
            ) {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            if (
              header.key === 'registered_value' ||
              header.key === 'registered_aov' ||
              header.key === 'anonymous_aov' ||
              header.key === 'total_value'
            ) {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `registered_activity_by_location_${periodMode}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.state.data || [];
    const periodLabel = this.state.period?.label || 'Last 30d';

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.5, sm: 2 },
            '&:last-child': { pb: { xs: 1.5, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Registered activity by location
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: '0.76rem', sm: '0.82rem' },
                  }}
                >
                  Registered client contribution by restaurant · {periodLabel}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                disabled={!rows.length}
                onClick={this.handleExportCsv}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  minWidth: 64,
                  px: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  color: '#111827',
                  borderColor: 'rgba(15, 23, 42, 0.14)',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'rgba(15, 23, 42, 0.24)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0,
                  },
                }}
              >
                CSV
              </Button>
            </Stack>

            <TableContainer
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: '12px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: { xs: 760, md: 960 },
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.72rem', sm: '0.78rem' },
                    py: { xs: 0.75, sm: 0.9 },
                    px: { xs: 0.8, sm: 1 },
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 800,
                    color: '#1a237e',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell align="right">Reg. orders</TableCell>
                    <TableCell align="right">Reg. sales</TableCell>
                    <TableCell align="right">Order share</TableCell>
                    <TableCell align="right">Sales share</TableCell>
                    <TableCell align="right">Reg. AOV</TableCell>
                    <TableCell align="right">Anon. AOV</TableCell>
                    <TableCell align="right">Total orders</TableCell>
                    <TableCell align="right">Total sales</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`${row.location_iref || 'loc'}-${index}`} hover>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: '#111827',
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.location_name || 'Unknown'}
                      </TableCell>

                      <TableCell align="right">
                        {thousandsSeparator(Number(row.registered_orders || 0).toFixed(0))}
                      </TableCell>

                      <TableCell align="right">
                        {formatMoney(row.registered_value)}
                      </TableCell>

                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          <Box sx={{ width: 54 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(Number(row.registered_order_share || 0), 100)}
                              sx={{
                                height: 6,
                                borderRadius: 999,
                                bgcolor: 'rgba(26, 35, 126, 0.08)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 999,
                                  bgcolor: '#1a237e',
                                },
                              }}
                            />
                          </Box>

                          <Box sx={{ minWidth: 42, fontWeight: 700 }}>
                            {Number(row.registered_order_share || 0).toFixed(1)}%
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          <Box sx={{ width: 54 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(Number(row.registered_value_share || 0), 100)}
                              sx={{
                                height: 6,
                                borderRadius: 999,
                                bgcolor: 'rgba(26, 35, 126, 0.08)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 999,
                                  bgcolor: '#1a237e',
                                },
                              }}
                            />
                          </Box>

                          <Box sx={{ minWidth: 42, fontWeight: 700 }}>
                            {Number(row.registered_value_share || 0).toFixed(1)}%
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell align="right">
                        {formatMoney(row.registered_aov)}
                      </TableCell>

                      <TableCell align="right">
                        {formatMoney(row.anonymous_aov)}
                      </TableCell>

                      <TableCell align="right">
                        {thousandsSeparator(Number(row.total_orders || 0).toFixed(0))}
                      </TableCell>

                      <TableCell align="right">
                        {formatMoney(row.total_value)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No client activity data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default ClientsActivityByLocation;
